'use client';

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils/cn';

interface EditorRangeControlProps {
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
  format?: (value: number) => string;
  onChange: (value: number) => void;
  className?: string;
  ariaLabel: string;
}

/** One range language for every editor inspector: full-width track + fixed value pill. */
export function EditorRangeControl({
  min,
  max,
  step,
  value,
  unit,
  format,
  onChange,
  className,
  ariaLabel,
}: EditorRangeControlProps) {
  const safeMax = Math.max(min, max);
  const safeValue = Math.min(safeMax, Math.max(min, Number.isFinite(value) ? value : min));
  const progress = safeMax > min ? ((safeValue - min) / (safeMax - min)) * 100 : 0;
  const displayValue = format
    ? format(safeValue)
    : `${Number.isInteger(step) ? Math.round(safeValue) : safeValue.toFixed(1)}${unit ?? ''}`;

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <input
        type="range"
        min={min}
        max={safeMax}
        step={step}
        value={safeValue}
        aria-label={ariaLabel}
        onChange={(event) => onChange(Number(event.target.value))}
        className="editor-range h-5 min-w-0 flex-1 cursor-pointer appearance-none bg-transparent"
        style={{ '--editor-range-progress': `${progress}%` } as CSSProperties}
      />
      <output className="min-w-[52px] shrink-0 rounded-md bg-white/[0.06] px-2 py-1 text-center text-[10px] font-semibold tabular-nums text-white/65">
        {displayValue}
      </output>
    </div>
  );
}
